/**
 * Camera QR scanner (getUserMedia + native BarcodeDetector). Zero-knowledge:
 * what is scanned is a device id (public routing); the content stays E2E.
 *
 * REAL scanning with a physical camera is MANUAL validation (headless has no
 * real camera; the e2e covers the copy/paste code path). If the
 * browser does not support BarcodeDetector, the user is invited to type the code.
 */

import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { X } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

function getDetectorCtor(): BarcodeDetectorCtor | undefined {
  const ctor: unknown =
    typeof globalThis === "object" ? Reflect.get(globalThis, "BarcodeDetector") : undefined;
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return typeof ctor === "function" ? (ctor as BarcodeDetectorCtor) : undefined;
}

export function isQrScanSupported(): boolean {
  return (
    getDetectorCtor() !== undefined && typeof navigator?.mediaDevices?.getUserMedia === "function"
  );
}

export function QrScanner(props: { onResult: (value: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [error, setError] = createSignal("");
  let video: HTMLVideoElement | undefined;
  let stream: MediaStream | undefined;
  let raf = 0;
  let stopped = false;

  onMount(() => {
    void (async () => {
      const Ctor = getDetectorCtor();
      if (!Ctor) {
        setError(t(msg`This browser can't scan QR codes — type the code instead.`));
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
      } catch {
        setError(t(msg`Camera unavailable — type the code instead.`));
        return;
      }
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
      const detector = new Ctor({ formats: ["qr_code"] });
      const tick = async () => {
        if (stopped || !video) {
          return;
        }
        try {
          const codes = await detector.detect(video);
          const value = codes[0]?.rawValue;
          if (value) {
            props.onResult(value);
            return;
          }
        } catch {
          /* frame with no code; keep going */
        }
        raf = requestAnimationFrame(() => void tick());
      };
      raf = requestAnimationFrame(() => void tick());
    })();
  });

  onCleanup(() => {
    stopped = true;
    if (raf) {
      cancelAnimationFrame(raf);
    }
    stream?.getTracks().forEach((track) => track.stop());
  });

  return (
    <div
      data-testid="qr-scanner"
      class="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-[var(--background)]/95 p-6 backdrop-blur-xl"
      role="dialog"
      aria-label={t(msg`Scan QR code`)}
    >
      <Show
        when={!error()}
        fallback={<p class="max-w-xs text-center text-sm text-muted-foreground">{error()}</p>}
      >
        <video
          ref={video}
          autoplay
          playsinline
          muted
          class="max-h-[60vh] w-full max-w-sm rounded-2xl border border-[var(--glass-border)] object-cover"
        />
        <p class="text-xs text-muted-foreground">{t(msg`Point the camera at a Susurro QR code`)}</p>
      </Show>
      <Button
        variant="outline"
        class="gap-2"
        data-testid="qr-scanner-close"
        onClick={props.onClose}
      >
        <X class="h-4 w-4" />
        {t(msg`Close`)}
      </Button>
    </div>
  );
}
